/**
 * Wire types for the BFF↔web seam. Kept dependency-free on purpose: the BFF
 * imports these but must NOT pull in the UI package (`@evt/flow-stream-ui`).
 * The SPA imports both and treats `RunDetail.events` as the UI package's
 * `TraceEvent[]` when rendering (see note on `TraceEventRecord`).
 *
 * Auth ceremony option/response objects are passed opaquely to/from
 * `@simplewebauthn/browser`, so they are typed as `unknown` here and cast at
 * the call site against `@simplewebauthn/types`.
 */

// ── Identity / session ───────────────────────────────────────────────────────

export interface PortalUser {
  id: string
  email: string
  displayName: string
}

export interface PortalContractor {
  id: string
  name: string
}

/** `POST /api/me/contractor`: move the session into another of the person's companies; answers with `MeResponse`. */
export interface SwitchContractorBody {
  contractorId: string
}

export interface MeResponse {
  user: PortalUser
  /** The session's active tenant — all runs/flows/conversations are scoped to it. */
  contractor: PortalContractor
  /** Every company the session may switch to (`POST /api/me/contractor`), oldest membership first. */
  contractors: PortalContractor[]
  /** The person's role in the ACTIVE tenant. */
  role: TenantRole
  /** Everything the server will allow this person in the active tenant — the SPA renders only these. */
  permissions: string[]
  /** EVT operator managing every tenant (PLATFORM_ADMIN_EMAILS). Grants no access to a tenant's runs. */
  platformAdmin: boolean
  /**
   * Signed in with a passkey or SSO. Access management needs it; a session that came from an e-mailed code does
   * not have it, and the SPA explains why the admin pages ask for a passkey.
   */
  strongAuth: boolean
}

/** Which login methods the BFF offers — drives the login page's buttons. */
export interface AuthMethodsResponse {
  passkey: boolean
  /** Present when the shared (legacy) OIDC provider is enabled; `label` feeds "Sign in with <label>". */
  oidc: { label: string } | null
  /** True when per-tenant SSO providers exist — the login page offers email-based discovery. */
  ssoDiscovery?: boolean
  /**
   * True when at least one tenant allows signing in with an e-mailed code. Says nothing about any person: whether
   * the code is actually sent is decided per address, and the response never reveals it.
   */
  emailCode?: boolean
}

/** GET /api/auth/sso/lookup?email= — home-realm discovery (domain-level only). */
export interface SsoLookupResponse {
  sso: { slug: string; label: string } | null
}

/** 204 (plain local logout) or 200 with an IdP end-session URL to navigate to. */
export interface LogoutResponse {
  redirectTo?: string
}

/**
 * `signin` = the one sign-in path (`POST /api/auth/start`): the code proves the mailbox, then the BFF decides what
 * the person gets (a session where a company allows codes, a passkey where one is needed). `login` = sign in with the
 * code itself; `register` / `add-device` bootstrap a passkey. The last three remain for links and bundles already out.
 */
export type EmailChallengePurpose = 'signin' | 'register' | 'add-device' | 'login'

/** UI locales the portal ships. The BFF uses this to localize outbound email. */
export type PortalLocale = 'en' | 'pt-BR'

export interface EmailChallengeBody {
  email: string
  purpose: EmailChallengePurpose
  /** Language of the requesting UI; the verification email is written in it. Defaults to 'en'. */
  locale?: PortalLocale
}

export interface EmailVerifyBody {
  tokenId: string
  code: string
}

/** POST /api/auth/start — the sign-in page's one question: "what is your work e-mail?" */
export interface SignInStartBody {
  email: string
  /** Language of the requesting UI; the code e-mail is written in it. Defaults to 'en'. */
  locale?: PortalLocale
}

/**
 * What happens next, decided by the BFF. `sso` depends on the e-mail's DOMAIN only (which company uses SSO is public,
 * as with the lookup); `code` answers identically for every address, whether or not a code was sent.
 */
export type SignInStartResponse =
  | { next: 'sso'; provider: { slug: string; label: string } }
  | { next: 'code'; tokenId: string }

/**
 * After a `signin` code proves the mailbox, what a passkey means for THIS person — said only now, never before:
 *  - `required`: no session yet; their companies accept only a passkey (or SSO), so creating one is how they get in;
 *  - `recommended`: signed in, but something they can do needs a passkey (managing people, a passkey-only company);
 *  - `optional`: signed in; a passkey only spares them the code next time.
 */
export interface PasskeyAdvice {
  need: 'required' | 'recommended' | 'optional'
  /** Names of the person's companies that accept only a passkey (their code session does not reach them). */
  passkeyOnlyCompanies: string[]
  /** Administers a company, or is an EVT platform administrator: managing people needs a passkey. */
  admin: boolean
  /** Already has at least one passkey, on this device or another. */
  hasPasskeys: boolean
}

/**
 * `bootstrap` — go on to create a passkey; `session` — signed in with the code (purpose `login`); `signedIn` — the
 * `signin` purpose, which also opens a short window (the bootstrap cookie) to create a passkey right away.
 */
export type EmailVerifyResponse =
  | { bootstrap: true }
  | { session: true }
  | { signedIn: boolean; passkey: PasskeyAdvice }

export interface RegisterVerifyBody {
  /** RegistrationResponseJSON from @simplewebauthn/browser. */
  attResp: unknown
  label?: string
}

export interface LoginVerifyBody {
  /** AuthenticationResponseJSON from @simplewebauthn/browser. */
  authResp: unknown
}

export interface CredentialSummary {
  id: string
  label: string
  createdAt: string
  lastUsedAt?: string
  deviceType: 'singleDevice' | 'multiDevice'
  backedUp: boolean
}

// ── Flow catalog ─────────────────────────────────────────────────────────────

export type FlowMode = 'fast' | 'deep'

export interface PortalFlow {
  flowId: string
  displayName: string
  description?: string
  mode: FlowMode
  /** humanInLoop.enabled || mode === 'deep' — surfaces the "may ask you questions" hint. */
  maybeAsksQuestions: boolean
  /** humanInLoop.maxRounds — drives the "Round N of M" pill on the suspension card. */
  maxRounds?: number
  /** Whether this flow has an enabled config for the session's contractor. */
  enabled: boolean
}

// ── Runs (executions) ────────────────────────────────────────────────────────

export type RunStatus = 'success' | 'partial_success' | 'failure' | 'awaiting_user_response' | 'running'

export interface RunIssue {
  code: string
  message: string
  stage?: string
  retryable?: boolean
}

export interface RunSummary {
  executionId: string
  flowId: string
  displayName: string
  status: RunStatus
  startedAt: string
  finishedAt?: string
  durationMs?: number
  issues: RunIssue[]
  summary?: Record<string, unknown>
  /** First ~280 chars of the brief — present only for portal-launched runs. */
  briefPreview?: string
  /** Email of the portal user who launched the run (multi-user tenants). */
  startedBy?: string
  /** Deliverable file count from the execution record (attachmentsOut). */
  filesOut?: number
  /**
   * Present only while a run whose last reply failed can still be answered: until `until`, at most `attemptsLeft`
   * more times. Its status stays `failure`; the BFF sends this only while it holds against the clock.
   */
  resumable?: RunResumable
}

/** A trace event as persisted by the backend; opaque to the contract, rendered by the UI package. */
export type TraceEventRecord = Record<string, unknown>

/** Pending HITL suspension reconstructed from the backend so a reply can be made on a fresh load. */
export interface PendingSuspension {
  suspensionId: string
  toolUseId: string
  toolName: string
  /** Full validated tool input (e.g. request_human_input prompt + responseSchema + suggestions). */
  toolInput: unknown
  expiresAt: string
  roundCount: number
}

/** When and how often a run whose last reply failed can still be answered. */
export interface RunResumable {
  until: string
  attemptsLeft: number
}

export interface RunDetail extends RunSummary {
  events?: TraceEventRecord[]
  eventsTruncated?: number
  /** Present when the run can be answered: awaiting, or `resumable`. */
  pendingSuspension?: PendingSuspension
  maxRounds?: number
  /**
   * What was asked: the persisted launch input + HITL replies. Present only
   * for portal-launched runs (mail-reader runs have no BFF input record).
   */
  input?: RunInputView
}

/** Query params of GET /api/runs (all optional; upstream paging is cursor-based). */
export interface RunListQuery {
  status?: RunStatus
  flowId?: string
  limit?: number
  cursor?: string
}

export interface Paginated<T> {
  items: T[]
  nextCursor?: string
}

/** Request body for POST /api/playground/execute (the SPA never sends contractorId). */
export interface StartRunBody {
  flowId: string
  brief: string
  parsedCommands?: Record<string, unknown>
  /**
   * Uploaded files by `uploadId` (ADR-0003) — what this SPA sends — or, from bundles older than uploads, files inline.
   */
  attachments?: Array<UploadedAttachmentRef | { name: string; mimetype: string; size: number; contentBase64: string }>
}

export interface CancelRunBody {
  reason?: string
}

export interface CancelRunResponse {
  ok: true
  /**
   * How the cancel took effect:
   *  - 'suspension_cancelled' — the run was awaiting a reply; the suspension was tombstoned upstream.
   *  - 'aborted'             — the run was streaming on this BFF; its upstream connection was aborted.
   */
  mode: 'suspension_cancelled' | 'aborted'
}

// ── Home overview (the dashboard aggregate behind GET /api/home) ────────────

/** An awaiting run surfaced on Home, enriched with the pending question. */
export interface HomeAttentionRun extends RunSummary {
  /** The question the model is waiting on (request_human_input `prompt`), when recoverable. */
  question?: string
  /** When the run paused to ask (suspension createdAt). */
  askedAt?: string
  /** When the pending question expires and the run finishes best-effort. */
  expiresAt?: string
}

export interface HomeStatsDay {
  /** Local calendar date (YYYY-MM-DD) in the requested tz offset. */
  date: string
  /** Runs STARTED that day, any status. */
  count: number
}

/**
 * Month-to-date aggregates, computed in the caller's timezone (the SPA passes
 * `Date.getTimezoneOffset()` so "this month" and day buckets match the user's
 * calendar, not UTC). All counts are scoped to the session's contractor.
 *
 * Attribution model: terminal aggregates (runsCompleted, successRate,
 * docsDelivered, medianDurationMs) are keyed on FINISH time — the outcome
 * counts in the month it happened; only the `runsPerDay` activity histogram
 * is keyed on start time.
 */
export interface HomeStats {
  /** ISO instant the current local month started at. */
  monthStart: string
  /** Terminal runs (success + partial_success + failure) FINISHED this month. */
  runsCompleted: number
  /** success + partial_success subset of `runsCompleted`. */
  succeeded: number
  /** succeeded / runsCompleted; null when nothing finished yet. */
  successRate: number | null
  /** Σ deliverable files over runs finished this month. */
  docsDelivered: number
  /**
   * Files delivered in the trailing 7 days, clamped to the current month —
   * never exceeds `docsDelivered` (it annotates that headline in the UI).
   */
  docsDeliveredLast7Days: number
  /** Median duration of delivering runs (success + partial) finished this month. */
  medianDurationMs: number | null
  /**
   * Terminal runs finished in the SAME elapsed slice of the previous month
   * (fair pace comparison); null when the aggregation window was cut short.
   */
  runsCompletedPrevMonthToDate: number | null
  /** Exactly 14 entries, oldest → newest, today (local) last. Zero-filled. */
  runsPerDay: HomeStatsDay[]
  /** Set when the upstream paging cap was hit — counts are lower bounds. */
  truncated?: boolean
}

/**
 * Memories of the signed-in person's own that are waiting on them, summed over
 * every Smith they may use.
 *
 * Absent — not zero — when the count could not be read. Zero is a real answer
 * ("nothing is waiting"), so reporting one for an outage would quietly retire a
 * card that exists to say there is something to do.
 */
export interface HomeMemoriesWaiting {
  total: number
  /** Where most of them are, so the card can link straight to that Smith. */
  flowId?: string
  displayName?: string
}

/** Everything the Home screen renders, in one round-trip. */
export interface HomeOverview {
  /** Awaiting runs, newest first (the hero "needs you" cards). */
  attention: HomeAttentionRun[]
  /** Running runs, newest first. */
  live: RunSummary[]
  /** Latest terminal runs, most recently FINISHED first (the "Latest results" list). */
  recent: RunSummary[]
  stats: HomeStats
  /**
   * Per-PERSON, so it is computed outside the per-contractor cache the rest of
   * this aggregate is served from — one cached count would show a colleague's.
   */
  memoriesWaiting?: HomeMemoriesWaiting
  generatedAt: string
}

// ── Run inputs (what was asked — persisted by the BFF at launch/resume) ─────

/** One HITL reply as persisted on the input record. */
export interface RunInputReplyView {
  /** Ordinal in the conversation; also the path segment for attachment downloads. */
  index: number
  at: string
  trigger: 'reply' | 'timeout' | 'cancel'
  rawBody: string | null
  /** Bytes via GET /api/runs/:id/input/replies/:replyIndex/files/:index. */
  attachments: RunFileMeta[]
}

/**
 * The persisted request side of a run: the brief + attachments captured at
 * launch and every HITL reply appended on resume. Attachment bytes are served
 * by GET /api/runs/:id/input/files/:index (launch) and the reply route above.
 */
export interface RunInputView {
  executionId: string
  brief: string
  briefTruncated?: boolean
  parsedCommands?: Record<string, unknown>
  attachments: RunFileMeta[]
  replies: RunInputReplyView[]
  startedBy?: { email: string; displayName?: string }
  createdAt: string
}

// ── Uploads (ADR-0003) ─────────────────────────────────────────────────────────

/**
 * What one file and one launch or reply may carry, and the part size the browser uploads in. Read from
 * `GET /api/uploads/limits`: the SPA never hard-codes a limit.
 */
export interface UploadLimits {
  partBytes: number
  maxFileBytes: number
  maxTotalBytes: number
  maxFiles: number
}

/** An upload session as the BFF reports it (`POST /api/uploads`, `GET /api/uploads/:id`, parts, completion). */
export interface UploadView {
  uploadId: string
  name: string
  mimetype: string
  size: number
  partBytes: number
  partCount: number
  /** Parts the BFF holds, ascending. A client resuming an upload sends the others. */
  receivedParts: number[]
  status: 'open' | 'ready'
  /** Lower-case hex SHA-256 of the stored file, once `ready`. */
  sha256?: string
  expiresAt: string
}

/** How a launch or a reply names an uploaded file. The BFF reads the rest from the upload itself. */
export interface UploadedAttachmentRef {
  uploadId: string
  /** Echoed for the client's own bookkeeping; the server uses the upload's stored values. */
  name: string
  mimetype: string
  size: number
}

// ── Run results (deliverables persisted by the BFF) ─────────────────────────

/** One deliverable file's metadata; bytes come from GET /api/runs/:id/files/:index. */
export interface RunFileMeta {
  index: number
  name: string
  mimetype: string
  size: number
}

/**
 * The persisted outcome of a finished run — captured by the BFF from the
 * NDJSON `final`/`error` envelope as it streamed, so deliverables survive
 * tab closes and are downloadable from history. `GET /api/runs/:id/result`
 * answers 404 when there is none: code `result_pending` while the BFF is
 * still preparing it (ask again), `result_not_found` otherwise.
 */
export interface RunResultView {
  executionId: string
  status: 'success' | 'partial_success' | 'failure'
  reply?: { subject?: string; message?: string }
  issues: RunIssue[]
  files: RunFileMeta[]
  /** ISO-8601 when the result was captured. */
  createdAt: string
}

/** Header the BFF sets on POST /api/playground/execute so the SPA learns the run id immediately. */
export const EXECUTION_ID_HEADER = 'X-Execution-Id'

// ── Answer rating (public — the signed link is the credential) ──────────────

/** A rating on the 1–5 scale; stars in the e-mail pre-select one. */
export type FeedbackScore = 1 | 2 | 3 | 4 | 5

export interface FeedbackRatings {
  responseTime?: FeedbackScore
  assertiveness?: FeedbackScore
}

/** Body of POST /api/public/feedback/context. */
export interface FeedbackContextBody {
  token: string
}

/**
 * What the public rating page may know about the answer being rated — the minimum the
 * token authorizes (fr-docs E5-R03): no contractor, no content of the answer.
 */
export interface FeedbackContextResponse {
  flowId: string
  /** The flow's display name (flow-default row), humanized from the id when it has none. */
  flowName: string
  /** Locale the answer was written in; the page opens in it unless the reader chose one. */
  locale: string
  /** What is being rated (ticket ref + title), when the answer carried one. */
  reference: string | null
  /** ISO-8601 when the answer was delivered, when known. */
  answeredAt: string | null
  /** A previous submission through the same link; the form is editable until the link expires. */
  ratings: FeedbackRatings | null
  suggestions: string | null
  submissions: number
}

/**
 * Body of POST /api/public/feedback/submit — at least one rating or a suggestion. An empty
 * `suggestions` clears a previous one; fr-docs takes it only beside a rating.
 */
export interface FeedbackSubmitBody extends FeedbackRatings {
  token: string
  suggestions?: string
}

export interface FeedbackSubmitResponse {
  ratings: FeedbackRatings
  submissions: number
}

/** fr-docs caps suggestions at 2,000 characters; the page and the BFF enforce the same. */
export const FEEDBACK_SUGGESTIONS_MAX = 2_000

/** fr-docs refuses a longer token outright (`verifyFeedbackToken`). */
export const FEEDBACK_TOKEN_MAX_CHARS = 2_000

/**
 * The shape of fr-docs' rating token (`domain/feedback/token.ts`):
 * `v1.<base64url payload>.<base64url HMAC-SHA256>`, whose 32-byte MAC is 43 characters
 * unpadded. A shape check only: fr-docs alone holds the key that verifies a token. The BFF
 * refuses any other string before calling fr-docs, and the SPA reads only this shape.
 */
const FEEDBACK_TOKEN_SHAPE = /^v1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43}$/

export function isFeedbackTokenShape(token: unknown): token is string {
  return typeof token === 'string' && token.length <= FEEDBACK_TOKEN_MAX_CHARS && FEEDBACK_TOKEN_SHAPE.test(token)
}

// ── Memory (what a Smith remembers, as the person it belongs to sees it) ────

/**
 * What kind of thing a memory is. These are the service's own classes; the SPA
 * has a word for each in both languages and falls back to no label at all for a
 * value it does not know, rather than showing a raw token.
 */
export type PortalMemoryKind = 'preference' | 'correction' | 'decision' | 'procedure' | 'fact' | 'negative'

/**
 * One memory, in the customer's terms.
 *
 * Deliberately narrow. There is no triple, no tier, no confidence, no
 * corroboration count and above all **no author**: a company's memory showing
 * "taught by Ana" tells one colleague what another typed into a Smith. The
 * service's own response shape leaves the author out for the same reason, so
 * this type carrying it would be the second mistake, not the first.
 */
export interface PortalMemory {
  id: string
  /** The memory's details; empty when the sentence says it all. */
  text: string
  /** The whole memory in one sentence. Absent on a memory written before one was asked for. */
  title?: string
  kind: PortalMemoryKind | string
  /** Whether the Smith is using it yet. A personal memory that is not is waiting on its owner. */
  inForce: boolean
  savedAt: string
  /** Carried back on a change, so a memory that moved meanwhile is refused rather than overwritten. */
  version: number
}

/** One Smith's memories, as one person sees them. */
export interface PortalMemoryGroup {
  flowId: string
  displayName: string
  /** The company's, in use, in nobody's name. Read-only here — an operator governs these. */
  shared: PortalMemory[]
  /** This person's own: waiting first, because that is the part they can act on. */
  personal: PortalMemory[]
  /** How many of `personal` are waiting on this person. */
  waiting: number
  /** The upstream read hit its ceiling, so these lists may be short. */
  truncated: boolean
  /** This Smith's memories could not be read. The other Smiths still render. */
  unavailable?: boolean
  /**
   * How full this Smith's memory is for the company: `ok`, `near` (from 80% of its
   * limit) or `full`. A word and never a number — the limit is counted in units a
   * customer has no reason to learn. At `full`, what the Smith learns waits, and a
   * memory can only be used or added once another is removed. Absent when it could
   * not be read.
   */
  capacity?: 'ok' | 'near' | 'full'
}

/**
 * What a memory a person writes may hold, in characters, as the service decides it. Read with the memories rather than
 * copied into the portal: the values are starting points the service's operator expects to move.
 */
export interface PortalMemoryLimits {
  titleMaxChars: number
  bodyMaxChars: number
  /** The longest details shown with the sentence; longer ones are read when needed. Never a limit on the sentence. */
  bodyInlineMaxChars: number
}

/** Every Smith's memories in one call — what the Memories screen renders. */
export interface PortalMemoryOverview {
  /** One per visible Smith that keeps memories for the company; empty when none does. */
  groups: PortalMemoryGroup[]
  /** Σ of every group's `waiting`. */
  waiting: number
  /** Absent when no Smith's memories could be read, or the service is older than them. */
  limits?: PortalMemoryLimits
}

/**
 * The three kinds of memory a person may write about themselves.
 *
 * Three of the service's six classes, and the choice is theirs: a sentence like
 * "never quote prices in dollars to me" IS a prohibition, and a prohibition is
 * the one kind whose omission leaves the assistant WRONG rather than merely less
 * informed — so storing every typed sentence as a preference cost exactly that.
 * The other three classes are about the work rather than about the person.
 *
 * The word written and the word read back differ on purpose: the service stores
 * a `prohibition` as its class `negative`, so the memory comes back listed as
 * `negative` (see `PortalMemoryKind`).
 */
export type PortalMemoryKindChoice = 'preference' | 'prohibition' | 'fact'

/** Body for the three decisions a person may take on a memory of their own. */
export interface MemoryActionBody {
  flowId: string
  /** The version last read; omitting it takes whatever is there now. */
  version?: number
}

export interface MemoryRejectBody extends MemoryActionBody {
  /** Required: a refusal is terminal, so it records why. */
  reason: string
}

export interface MemoryEditBody extends MemoryActionBody {
  /** The details; may be empty. */
  text: string
  /** A new sentence. Absent leaves it as it is; blank is none, which the service refuses. */
  title?: string
}

/** What a write answers with: the memory as it now stands, or that it is closed. */
export type MemoryWriteResult = { memory: PortalMemory } | { closed: true }

// ── Access management (tenant admins + platform admins) ─────────────────────

export type TenantRole = 'member' | 'admin'

export interface AccessTenantSettings {
  /** People of this tenant may sign in with an e-mailed one-time code instead of a passkey. */
  emailCodeLogin: boolean
  /** When non-empty, only addresses under these domains may be invited. */
  allowedEmailDomains: string[]
}

/**
 * Which flows a company has. `all`: every flow published to the portal for it, including flows published later (EVT's
 * own company, and every company that predates the choice). `selected`: only `flowIds` — a flow published later stays
 * out until it is chosen. What its people see is these AND what fr-docs offers the company (its flow rows).
 */
export type TenantFlowAccessMode = 'all' | 'selected'

export interface TenantFlowAccess {
  mode: TenantFlowAccessMode
  /** The chosen flows; kept under `all` too, so switching back to `selected` restores the choice. */
  flowIds: string[]
  /** Bumped on every change; 0 = never chosen. A write carrying another version is refused (`flow_access_conflict`). */
  version: number
}

export interface AccessTenant {
  /** The contractor id — identical in fr-docs, mail-reader and Key Vault. */
  id: string
  name: string
  status: 'active' | 'suspended'
  settings: AccessTenantSettings
  /** Which flows the company has (set by EVT platform administrators). */
  flowAccess: TenantFlowAccess
  /** False for a tenant known only through memberships (no registry row yet — defaults apply). */
  registered: boolean
  /** `active` = people with access; `admins` and `invited` (never signed in yet) are subsets of it. */
  counts: { active: number; admins: number; invited: number; revoked: number }
  updatedAt?: string
}

export interface AccessMember {
  userId: string
  email: string
  displayName: string
  role: TenantRole
  /** `invited` = active membership, never signed in yet. */
  status: 'active' | 'invited' | 'revoked'
  /** Who granted the membership: an invitation, the e-mail domain map, or an SSO/Entra authority. */
  source: 'invite' | 'email-domain' | 'entra-tenant' | 'sso-provider' | 'legacy'
  invitedAt?: string
  invitedByEmail?: string
  lastLoginAt?: string
  revokedAt?: string
  /** Registered passkeys (devices). */
  passkeys: number
  /** Linked to an SSO identity. */
  sso: boolean
  /** Active memberships in OTHER tenants — a tenant admin cannot reset passkeys when > 0. */
  otherTenants: number
}

export interface InviteRequest {
  emails: string[]
  role: TenantRole
  /** Send the invitation e-mail (default true). */
  sendEmail?: boolean
  locale?: 'en' | 'pt-BR'
}

export interface InviteResult {
  email: string
  outcome: 'invited' | 'reactivated' | 'already_member'
  userId: string
  emailed: boolean
  emailError?: string
}

export interface UpdateMemberRequest {
  role?: TenantRole
  status?: 'active' | 'revoked'
}

export interface AccessAuditEntry {
  id: string
  at: string
  action: string
  actorEmail: string
  actorIsPlatform: boolean
  targetEmail?: string
  detail?: Record<string, unknown>
}

export interface CreateTenantRequest {
  id: string
  name: string
  settings?: Partial<AccessTenantSettings>
  /** Default: `selected` with nothing chosen — the company sees no flow until one is chosen on its page. */
  flowAccess?: Pick<TenantFlowAccess, 'mode' | 'flowIds'>
  /** Appointed as the tenant's first administrator and invited. */
  firstAdminEmail?: string
  sendEmail?: boolean
  locale?: 'en' | 'pt-BR'
}

export interface UpdateTenantRequest {
  name?: string
  status?: 'active' | 'suspended'
  settings?: Partial<AccessTenantSettings>
}

/** One flow on a company's page: offered to it by fr-docs, chosen for it in the portal, or both. */
export interface TenantFlowOption {
  flowId: string
  displayName: string
  description?: string
  /** fr-docs offers it to this company: it can run it, it is not internal, and it is published to the portal. */
  offered: boolean
  /**
   * Its flow row says `portalVisible: true`. False on an offered flow only where the portal lists every flow
   * (`PORTAL_FLOW_VISIBILITY=all`): it is offered because of the environment, not because anyone published it.
   */
  published: boolean
  /** Chosen for the company. A chosen flow that is no longer offered is shown so it can be removed. */
  chosen: boolean
}

/** The Flows card of a company's page (platform administrators). */
export interface TenantFlowsView {
  tenantId: string
  mode: TenantFlowAccessMode
  version: number
  /** Offered flows in fr-docs' order, then chosen flows that are no longer offered. */
  flows: TenantFlowOption[]
  /** Set when fr-docs could not be asked: `flows` then holds only the chosen ones, with `offered` unknown (false). */
  offeredError?: string
}

export interface UpdateTenantFlowsRequest {
  mode: TenantFlowAccessMode
  flowIds: string[]
  /** The version the change was made on (`TenantFlowsView.version`); omitted, the change applies to whatever is there. */
  version?: number
}

/** What a tenant still needs before its people can use the portal end to end. */
export interface TenantReadiness {
  tenantId: string
  status: 'active' | 'suspended'
  /** The BFF holds this contractor's HMAC secret. */
  signingSecret: boolean
  /**
   * What fr-docs made of a signed probe: `accepted` = runs can start; `rejected` = fr-docs holds a different
   * secret (or none) for this contractor; `missing` = the BFF has none; `unreachable` = fr-docs could not be asked.
   */
  signing: 'accepted' | 'rejected' | 'missing' | 'unreachable'
  signingDetail?: string
  /** Flows the tenant's people will see (offered by fr-docs AND chosen for the company); null when fr-docs could not be asked. */
  flows: Array<{ flowId: string; name: string }> | null
  /** Whether the company has every offered flow or only the chosen ones. */
  flowAccessMode: TenantFlowAccessMode
  flowsError?: string
  admins: number
  activeMembers: number
  emailCodeLogin: boolean
  checks: Array<{
    key:
      | 'registered'
      | 'active'
      | 'signing_secret'
      | 'signing_accepted'
      | 'flows_chosen'
      | 'flows_visible'
      | 'has_admin'
    ok: boolean
  }>
}
